import { action, flow, getParentOfType, register, types } from "@gadgetinc/mobx-quick-tree";
import { toaster } from "baseui/toast";
import { isNil } from "lodash";
import { GadgetDomainModelClass } from "state-trees/src/utils";

export const ChatRole = types.enumeration("Role", ["user", "system"]);
const errorAnswer = "Sorry, there was an error with the chatbot. Please try again later.";

/** Represents one message from either party in the chat */
@register
export class DocsChatMessage extends GadgetDomainModelClass("DocsChatMessage", {
  role: ChatRole,
  message: types.string,
  loading: types.optional(types.boolean, false),
  isError: types.optional(types.boolean, false),
  isSavingRating: types.optional(types.boolean, false),
  userRating: types.maybeNull(types.boolean),
  userRatingComment: types.maybeNull(types.string),
  references: types.array(types.frozen<{ url: string; title: string }>()),
}) {
  get rateable() {
    return this.role === "system" && !this.loading && !this.isError;
  }

  get showReferences() {
    return !this.loading && this.references.length > 0;
  }

  @action
  setError(errorMessage = errorAnswer) {
    this.isError = true;
    this.message = errorMessage;
  }
  @action
  setMessage(message: string) {
    this.message = message;
  }
  @action
  appendMessage(chunk: string) {
    this.message += chunk;
  }
  @action
  setUserRating(isGood: boolean) {
    this.userRating = isGood;
  }
  @action
  setUserRatingComment(comment: string) {
    this.userRatingComment = comment;
  }
  @action
  setReferences(references: { url: string; title: string }[]) {
    this.references.replace(references);
  }

  @action
  saveRating = flow(function* (this: DocsChatMessage) {
    if (isNil(this.userRating)) return;
    this.isSavingRating = true;
    try {
      const res = yield fetch(`${(window as any).GadgetDocsConfig.servicesRootURL}/chat/rate-message`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationKey: getParentOfType(this, DocsChatbotSession).key,
          messageKey: this.key,
          isGood: this.userRating,
          comment: this.userRatingComment,
        }),
      });

      if (!res.ok || !res.body) {
        toaster.negative("There was an error submitting your rating, please try again", { autoHideDuration: 2000 });
        return;
      }
    } finally {
      this.isSavingRating = false;
    }
  });
}

/** State manager for a session with the chat backend */
@register
export class DocsChatbotSession extends GadgetDomainModelClass("DocsChatbotSession", {
  messages: types.array(DocsChatMessage),
  docsVersion: types.maybe(types.string),
  prompt: types.optional(types.string, ""),
  started: types.optional(types.boolean, false),
  loading: types.optional(types.boolean, false),
  error: types.maybe(types.string),
}) {
  canSubmit() {
    return this.prompt != "";
  }

  @action
  setPrompt(input: string) {
    this.prompt = input;
  }

  @action
  submitPrompt = flow(function* (this: DocsChatbotSession) {
    if (this.prompt == "") return;
    this.loading = true;
    this.started = true;
    const prompt = this.prompt;
    this.prompt = "";
    const promptMessage = DocsChatMessage.create({ role: "user", message: prompt });
    this.messages.push(promptMessage);
    const nextMessage = DocsChatMessage.create({ role: "system", message: "", loading: true });
    this.messages.push(nextMessage);

    try {
      const res: Response = yield fetch(`${(window as any).GadgetDocsConfig.servicesRootURL}/chat/message`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          docsVersion: this.docsVersion,
          conversationKey: this.key,
          promptMessageKey: promptMessage.key,
          responseMessageKey: nextMessage.key,
          prompt,
        }),
      });

      if (!res.ok || !res.body) {
        nextMessage.setError();
        return;
      }

      const stream = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let done = false;

      while (!done) {
        const { value, done: doneReading } = yield stream.read();
        done = doneReading;
        if (value) {
          nextMessage.appendMessage(value);
        }
      }
    } catch (e) {
      console.error("[chat] Error processing chatbot response stream", e);
      nextMessage.setError(errorAnswer);
    } finally {
      nextMessage.loading = false;
      this.loading = false;

      const matches = [...nextMessage.message.matchAll(/\[\[(\d+)]]\((.*?)\)/g)];

      const references = Array.from(new Set(matches?.map((match) => match[2]) || []));
      nextMessage.setReferences(references.map((ref) => ({ url: ref, title: ref })));
    }
  });
}
